import type { CSSProperties } from "react";

import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { RichTextViewer } from "simplydo/react/richtext";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Message } from "simplydo/react/ui/message";

import util from "utils/utils";

type TermsCheckboxProps = {
  organisation: OpenAPI.Schemas["Organisation"] | null;
  agreed: boolean;
  onAgree: (checked: boolean) => void;
  agreedCustomTerms: boolean;
  onAgreeCustomTerms: (checked: boolean) => void;
  style?: CSSProperties;
};

const TermsCheckbox = ({
  organisation,
  agreed,
  agreedCustomTerms,
  onAgree,
  onAgreeCustomTerms,
  style,
}: TermsCheckboxProps) => {
  const { t } = useTranslation();

  if (!organisation) {
    return null;
  }

  const customTerms = organisation.customTerms;

  return (
    <div style={{ ...style }}>
      <H4>
        {t("entry.terms.terms")} & {t("entry.terms.privacy")}
        <Subheader>
          {t("entry.terms.instruction", { appName: util.appName() })}{" "}
          <a
            href="https://simplydo.co.uk/terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("entry.terms.terms")}
          </a>{" "}
          {t("generic.and")}{" "}
          <a
            href="https://simplydo.co.uk/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("entry.terms.privacy")}
          </a>
          .
        </Subheader>
      </H4>

      <div style={{ paddingLeft: 20, padding: 10 }}>
        <Checkbox checked={agreed} onChange={(_e, c) => onAgree(!!c.checked)}>
          {t("entry.terms.iAgree")}
        </Checkbox>
      </div>

      {customTerms?.enabled && customTerms?.terms && (
        <>
          <Divider hidden />
          <H4>
            {t("entry.terms.additionTermsTitle", {
              orgName: organisation?.name,
            })}
          </H4>
          <RichTextViewer value={customTerms?.terms} />
          <div style={{ paddingLeft: 20, padding: 10, marginTop: 10 }}>
            <Checkbox
              style={{ textAlign: "left" }}
              checked={agreedCustomTerms}
              onChange={(_c, data) => {
                onAgreeCustomTerms(!!data.checked);
              }}
            >
              {t("entry.terms.iAgreeCustomTerms", {
                orgName: organisation?.name,
              })}
            </Checkbox>
          </div>
        </>
      )}
      <Message size="small">{t("entry.terms.info")}</Message>
    </div>
  );
};

export default TermsCheckbox;
